// Jogosultságkezelés (ADMIN) — STANDARD tier-modell (a szerep hordozza a default jogokat; a per-user CSAK
// kivétel). A KÖZÖS full-page lista-mintán (clients.index a referencia), füles (Tabs) elrendezésben — MINDKÉT
// fül teljes-magasságú (AppShell main → h-full flex-col wrapper → DataTable flex-1, tab-onként):
//   • SZEREPEK — a tier feature-jogai; a szuperadmin szerkesztheti a member/admin tier alap-jogait
//     (`access_rights.manage` — „Jogosultságok kezelése"); a `superadmin` szerep RÖGZÍTETT (teljes jog).
//   • TAGOK — szerepük (tier, váltható) + PER-USER FELÜLÍRÁSAIK (grant=többlet / deny=a szerep-jog elvétele).
//     A felülírás-szerkesztő az EFFEKTÍV állapotot állítja, a rendszer ebből vezeti le a grant/deny override-ot.
// Page-gate: `access_rights.view` (superadmin-only; a BFF is ezt kéri; a kezelés-fal az Odiné — {ns}:admin /
// `rbac:admin` (kereszt-app RBAC-konzol) / platform:admin). A multi-app választó a `rbac:admin`-t (v. platform:admin) kéri.
// Minden felirat i18n-kulcsból; a jog-KULCS marad a szerződés (nem fordítjuk).
import {
  TrashIcon,
  LockClosedIcon,
  PencilSquareIcon,
  PlusIcon,
  ShieldCheckIcon,
  UserMinusIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';
import { useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { AppSelector } from '@/components/shared/app-selector';
import { AppShell } from '@/components/shared/app-shell';
import { AttentionBanner } from '@/components/shared/attention-banner';
import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { DomainBadge } from '@/components/shared/domain-badge';
import { EmptyState } from '@/components/shared/empty-state';
import { EntityCombobox, type ComboboxOption } from '@/components/shared/entity-combobox';
import { ExpandableCell } from '@/components/shared/expandable-cell';
import { ListErrorState } from '@/components/shared/list-error-state';
import { ListNewButton } from '@/components/shared/list-new-button';
import { RowActionButton, RowActions } from '@/components/shared/list-row-actions';
import { ALL_FILTER_VALUE, ListToolbar } from '@/components/shared/list-toolbar';
import { RoleIcons } from '@/components/shared/role-icons';
import { Avatar } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Segmented } from '@/components/ui/segmented';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Text } from '@/components/ui/text';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { api } from '@/lib/api';
import { dependentsOf, missingPrerequisites, viewPrerequisiteOf } from '@/lib/permission-deps';
import { requireAuth } from '@/lib/auth';
import { formatName } from '@/lib/format-name';
import { isConsoleManageableKey, meaningfulOverrides, roleLabel } from '@/lib/permission-catalog';
import { useAdminApps } from '@/lib/use-admin-apps';
import { usePermissions } from '@/lib/use-permissions';
import { cn, isForbiddenError } from '@/lib/utils';

export const Route = createFileRoute('/access-rights')({
  beforeLoad: () => requireAuth(),
  component: AccessRightsPage,
});

type Effect = 'grant' | 'deny';
interface Role {
  key: string;
  /**
   * `manifest` = kódból kezelt (a boot-sync felülírja/prune-olja) · `seed` = alap-szerep (a manifest hozta létre,
   * a tartalma itt szerkeszthető, a deploy nem írja vissza, nem törölhető) · `console` = felületen létrehozott.
   */
  source?: 'manifest' | 'seed' | 'console';
  /** A szerep KÉTNYELVŰ címkéje (az Odinból; a `name` oszlop megszűnt). Null → a UI a kulcs-tierre esik vissza. */
  label: { hu: string; en: string } | null;
  permissions: string[];
}
interface Member {
  identityId: string;
  email: string | null;
  displayName: string | null;
  givenName: string | null;
  familyName: string | null;
  type: string;
  avatarUrl: string | null;
  teamColor: string | null;
  roles: string[];
  permissions: string[];
  permissionOverrides: { key: string; effect: Effect }[];
}

// A belső tier-létra rangja (a szerepek rendezéséhez). A `superadmin` szerep RÖGZÍTETT (mindig teljes jog).
// A portál-létra UGYANEZT a növekvő-privilégium sorrendet követi (a felület egységes: a legkevesebbtől a
// legtöbb jogig), különben a partner-fül a kulcsok ábécéjét mutatná — ott a `portal-admin` kerülne előre.
const TIER_RANK: Record<string, number> = {
  member: 0,
  admin: 1,
  superadmin: 2,
  'portal-viewer': 0,
  'portal-member': 1,
  'portal-admin': 2,
};
const rankOf = (key: string): number => TIER_RANK[key] ?? 0;

/**
 * A SZEREP-LISTA SORRENDJE — előbb a felületen létrehozottak ABC-ben, utána a kódból kezeltek a létra szerint.
 *
 * MIÉRT ÍGY: a kódból kezelt szerepek a rendszer GERINCE, a sorrendjük tanít (a legkevesebb jogtól a legtöbbig),
 * ezért ott a rang a helyes rendezés, nem az ábécé. A konzolon felvett szerepek viszont AD HOC halmaz — köztük
 * nincs létra, náluk az ábécé a kiszámítható. Elöl állnak, mert azok a „sajátjaink": azokat szerkesztjük, és
 * azokat kell megtalálni, míg a kódból kezelteket jellemzően csak olvassuk (a szerkesztésüket a deploy felülírja).
 */
const compareRoles =
  (locale: 'hu' | 'en') =>
  (a: Role, b: Role): number => {
    const aConsole = a.source === 'console';
    const bConsole = b.source === 'console';
    if (aConsole !== bConsole) return aConsole ? -1 : 1;
    if (aConsole) {
      return (a.label?.[locale] ?? a.key).localeCompare(b.label?.[locale] ?? b.key, locale);
    }
    return rankOf(a.key) - rankOf(b.key) || a.key.localeCompare(b.key);
  };
const LOCKED_ROLE = 'superadmin';

/**
 * A „Tag hozzáadása" dialógus kimenete: egy MÁR LÉTEZŐ személy + opcionális kezdő szerep.
 *
 * ÚJ SZEMÉLYT EZ A FELÜLET NEM HOZ LÉTRE (user-döntés): a konzol nem tudja, belsős munkatársat
 * vagy külsős kapcsolattartót vesznek-e fel, holott a kettőhöz MÁS adat tartozik (munkaviszony vs. ügyfél-kötés)
 * és MÁS felület kezeli. A dialógus két füle ezért nem „meglévő/új", hanem a FELÜLET: Munkatárs (`internal`)
 * vagy Külsős (`partner`) — a személy-választó ÉS a szerep-választó ehhez igazodik. A szűkítés a szerződésben
 * (és így a szerveren) is áll, nem csak a UI-ban elrejtve.
 */
interface AddMemberInput {
  /** A választott felület — a hívó ebből tudja, melyik lista/szerep-készlet volt érvényes (és hova navigáljon). */
  audience: 'internal' | 'partner';
  identityId: string;
  roleKey?: string;
}

interface RolesCache {
  status: number;
  body: { roles: Role[] };
}
interface MembersCache {
  status: number;
  body: { members: Member[] };
}

/** Egy tag megjelenítendő neve (locale-tudatos névsor; e-mail fallback). */
function memberName(m: Member, locale: string): string {
  return formatName(
    { givenName: m.givenName, familyName: m.familyName, displayName: m.displayName ?? m.email },
    locale,
  );
}

function AccessRightsPage(): JSX.Element {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const { can, isLoading: permsLoading, isPlatformAdmin, permissions } = usePermissions();
  // A SAJÁT identity-id — a védett jogok „magadnak soha" szabályának felület-oldali tükre.
  const sessionQ = api.session.useQuery(['session'], {});
  const session = sessionQ.data?.status === 200 ? sessionQ.data.body : undefined;
  const canView = can('access_rights.view');
  // Egyetlen „Jogosultságok kezelése" jog (access_rights.manage) fedi a szerep-alap-jog szerkesztést ÉS a
  // per-user felülírást is (a régi külön láthatósági/műveleti jog-kezelés helyett — RBAC-modell).
  const canManageRoleCatalog = can('access_rights.manage'); // szerep-KATALÓGUS (mit jelent a szerep) — mindkét felületen

  // KÖZPONTI (multi-app) konzol: a kezelt app (rejtett választónál mindig a saját = atlas). A Szerepek/Tagok
  // fülek + a szerkesztő-dialógusok VÁLTOZATLANUL a kiválasztott app adatán dolgoznak — a tier-alaplogika
  // (szerep=default, per-user grant/deny) appon belül sértetlen.
  const { apps, selectedApp, setSelectedApp, showSelector } = useAdminApps();

  // FELÜLET-VÁLTÓ (audience): `internal` = belső rendszer (munkatársak), `partner` = partner-felület (külsősök).
  // EGY helyen kezel MINDEN app MINDEN szerepét/jogát BELSŐ és KÜLSŐS felhasználókra: a választó a szerep/jog-
  // katalógust az Odin `?audience=`-re, a felhasználó-listát a `type`-ra szűri. Default: belső (a mai viselkedés).
  const [audience, setAudience] = useState<'internal' | 'partner'>('internal');
  const isPartner = audience === 'partner';
  /**
   * A PARTNER-FÜLÖN A SZEREP-RENDELÉS NEM ITT TÖRTÉNIK. A partner jogköre a `(személy, fiók)`
   * páré: ugyanaz a kapcsolattartó az egyik fiókján portál-admin, a másikon olvasó. Egy identity-központú
   * szerkesztő ezt nem tudja kifejezni, a globális rendelés pedig MINDEN fiókjára hatna — ezért a platform
   * 422-vel el is utasítja. A beállítás helye a FIÓK-oldal („Partner-portál hozzáférés" szekció); itt a
   * kereszt-fiókos, READ-ONLY áttekintő látszik, ugrással a fiókra.
   */
  const canManageRole = can('access_rights.manage') && !isPartner;
  /**
   * PER-USER FELÜLÍRÁS — a partner-fülön NINCS: a partner-jog globális grantját a platform 422-vel utasítja el
   * (a jogkör fiókonkénti), a globális DENY pedig önmagában félrevezető felület volna („elveszek egy jogot,
   * amit nem is itt adtam"). A partner-oldali kivétel-kezelés helye a fiók-oldali szerep.
   */
  const canManageOverride = can('access_rights.manage') && !isPartner;

  const portalOverviewQ = api.portalAccessOverview.useQuery(
    ['portal-access-overview'],
    {},
    { queryKey: ['portal-access-overview'], enabled: isPartner },
  );
  /** identityId → a személy fiókonkénti portál-szerepei (a partner-fül szerep-oszlopa). */
  const portalByIdentity = useMemo(() => {
    const map = new Map<
      string,
      { accountId: string; accountName: string | null; role: string }[]
    >();
    if (portalOverviewQ.data?.status !== 200) return map;
    for (const g of portalOverviewQ.data.body.grants) {
      const list = map.get(g.identityId) ?? [];
      list.push({ accountId: g.accountId, accountName: g.accountName, role: g.role });
      map.set(g.identityId, list);
    }
    return map;
  }, [portalOverviewQ.data]);

  const gated = !permsLoading && !canView;

  /**
   * VAN-E AZ APPNAK PARTNER-FELÜLETE? — ADAT-VEZÉRELTEN, a katalógusból: deklarál-e `audience=partner` szerepet.
   *
   * MIÉRT NEM `key === 'atlas'`: az mágikus konstans volna a UI-ban. A legtöbb Odin-consumer appnak nem lesz
   * partner-portálja, és amelyiknek lesz, ott a hozzáférés-modell valószínűleg NEM fiókonkénti (az az Atlas
   * tárgyköre) — a felület tehát ne kínáljon „Külsősök" nézetet olyan appnál, aminek egyetlen partner-szerepe
   * sincs: üres listát mutatna, és azt sugallná, hogy ott van mit beállítani. Ha egy app egyszer felveszi a
   * partner-szerepeit a manifestjébe, a fül MAGÁTÓL megjelenik — nincs mit átírni a konzolon.
   *
   * A lekérdezés kulcsa AZONOS a partner-fül szerep-lekérdezésével → a TanStack cache deduplikálja.
   */
  const partnerRolesQ = api.adminRoles.useQuery(['admin-roles', selectedApp, 'partner'], {
    query: { app: selectedApp, audience: 'partner' },
  });
  const hasPartnerSurface =
    partnerRolesQ.data?.status === 200 && partnerRolesQ.data.body.roles.length > 0;
  // App-váltás partner-nézetből olyan appra, aminek nincs partner-felülete → vissza a belső nézetre (különben
  // a lap egy nem létező felületet mutatna, üres listákkal).
  useEffect(() => {
    if (audience === 'partner' && partnerRolesQ.data?.status === 200 && !hasPartnerSurface) {
      setAudience('internal');
    }
  }, [audience, partnerRolesQ.data, hasPartnerSurface]);

  const rolesQ = api.adminRoles.useQuery(['admin-roles', selectedApp, audience], {
    query: { app: selectedApp, audience },
  });
  const membersQ = api.adminMembers.useQuery(['admin-members', selectedApp], {
    query: { app: selectedApp },
  });
  const assign = api.assignRole.useMutation();
  const revoke = api.revokeRole.useMutation();
  const updateRolePerms = api.updateRolePermissions.useMutation();
  const syncPerms = api.syncMemberPermissions.useMutation();
  // ── Tag- és szerep-ÉLETCIKLUS (IdP-konzol-minta) — mind `access_rights.manage` mögött, a kiválasztott appra ──
  const addMemberMut = api.addMember.useMutation();
  const removeMemberMut = api.removeMember.useMutation();
  const createRoleMut = api.createRole.useMutation();
  const deleteRoleMut = api.deleteRole.useMutation();

  const roles: Role[] = useMemo(() => rolesQ.data?.body.roles ?? [], [rolesQ.data]);
  // A felhasználó-listát a FELÜLET-VÁLTÓ szűri a `type`-ra: belső → `internal` munkatársak; partner → `partner`
  // (külsős) személyek. A service-fiók (pl. `svc-odin`) egyik felülethez sem tartozik → sosem jelenik meg itt.
  const memberType = isPartner ? 'partner' : 'internal';
  const members: Member[] = useMemo(
    () => (membersQ.data?.body.members ?? []).filter((m) => m.type === memberType),
    [membersQ.data, memberType],
  );

  const locale: 'hu' | 'en' = i18n.language === 'en' ? 'en' : 'hu';
  const sortedRoles = useMemo(() => [...roles].sort(compareRoles(locale)), [roles, locale]);
  const roleByKey = useMemo(() => new Map(roles.map((r) => [r.key, r])), [roles]);
  const roleKeys = sortedRoles.map((r) => r.key);

  // APP JOG-KATALÓGUS — az Odinból (minden app a SAJÁT GAZDAG metaadatát adja: kétnyelvű címke + modul-csoport +
  // sorrend). A konzol EGYSÉGESEN, adat-vezérelten címkéz MINDEN appra (az atlas is innen jön) — nincs kód-katalógus,
  // nincs vegyesség. Metaadat híján a kulcs a fallback.
  const permsQ = api.adminPermissions.useQuery(['admin-permissions', selectedApp, audience], {
    query: { app: selectedApp, audience },
  });
  const catalog = useMemo(
    () => (permsQ.data?.status === 200 ? permsQ.data.body.permissions : []),
    [permsQ.data],
  );
  const metaByKey = useMemo(() => new Map(catalog.map((p) => [p.key, p])), [catalog]);
  const catalogKeys = useMemo(() => catalog.map((p) => p.key), [catalog]);

  const labelFor = useCallback(
    (key: string): string => metaByKey.get(key)?.label?.[locale] ?? key,
    [metaByKey, locale],
  );
  // Egy szerep megjelenített neve: elsődlegesen az Odinból jött KÉTNYELVŰ `label` (locale szerint); ha nincs
  // (null), a UNIVERZÁLIS tier-i18n (`perm.role.{key}`), ami maga is a kulcsra esik vissza. Így a `name` megszűnt.
  const roleName = useCallback(
    // A CÍMKE-LÁNC: az Odin címkéje a nézett nyelven → az Odin címkéje BÁRMELY nyelven → az univerzális tier-
    // fordítás (`perm.role.*`) → végső esetben a kulcs. A második lépés nélkül egy csak egynyelvű címkével
    // érkező szerep (más app manifestje) NYERS kulcsként állt a jelvényen, holott volt emberi neve.
    (key: string): string => {
      const label = roleByKey.get(key)?.label;
      return (
        label?.[locale] ?? Object.values(label ?? {}).find((v) => v.trim() !== '') ?? roleLabel(key)
      );
    },
    [roleByKey, locale],
  );
  const posOf = useCallback(
    (key: string): number => metaByKey.get(key)?.position ?? 999,
    [metaByKey],
  );
  const sortKeys = useCallback(
    (keys: string[]): string[] => [...keys].sort((a, b) => posOf(a) - posOf(b)),
    [posOf],
  );
  // Adat-vezérelt modul-csoportosítás a szerkesztő-dialógusokhoz: a jog `group`-jából (fejléc a locale szerint),
  // a csoportok + a jogok sorrendje a `position` alapján.
  const buildGroups = useCallback(
    (
      keys: string[],
    ): { group: string; label: string; perms: { key: string; label: string }[] }[] => {
      const byGroup = new Map<
        string,
        { label: string; minPos: number; perms: { key: string; label: string }[] }
      >();
      for (const key of [...keys].sort((a, b) => posOf(a) - posOf(b))) {
        const g = metaByKey.get(key)?.group ?? null;
        const gKey = g?.key ?? 'other';
        const bucket = byGroup.get(gKey) ?? {
          label: g?.[locale] ?? gKey,
          minPos: posOf(key),
          perms: [],
        };
        bucket.perms.push({ key, label: labelFor(key) });
        byGroup.set(gKey, bucket);
      }
      return [...byGroup.entries()]
        .sort((a, b) => a[1].minPos - b[1].minPos)
        .map(([group, v]) => ({ group, label: v.label, perms: v.perms }));
    },
    [metaByKey, locale, posOf, labelFor],
  );

  // A kezelt app TÍPUSA (a választóból): `platform` (Odin-governance) → a katalógus SCOPE-kulcsai (platform:admin,
  // {ns}:admin, read:all/write:all) a szerkeszthetők; `consumer` (atlas/morgens-hub) → CSAK a FEATURE (pont-)kulcsok, a
  // colon-scope kulcsokat (atlas:admin/*:write) a BFF őrzi meg (a consumer-konzol UI-ja így VÁLTOZATLAN marad).
  const selectedKind = useMemo(
    () => apps.find((a) => a.key === selectedApp)?.kind ?? 'consumer',
    [apps, selectedApp],
  );
  // A teljes szerkeszthető univerzum: platform-appnál a KATALÓGUS-EGÉSZ (governance-scope-ok), consumer-appnál a
  // FEATURE-kulcsok. A `managedSet` ennek a halmaznak a tagsági tesztje — EZ dönti el app-agnosztikusan, mely
  // kulcsok a szerep/felülírás-szerkesztő „kezelt" jogai (a rajta kívüli kulcsokat — pl. infra-scope — megőrizzük).
  const featureUniverse = useMemo(
    () => (selectedKind === 'platform' ? catalogKeys : catalogKeys.filter(isConsoleManageableKey)),
    [catalogKeys, selectedKind],
  );
  const managedSet = useMemo(() => new Set(featureUniverse), [featureUniverse]);
  /**
   * VÉDETT JOGOK (`protected` a katalógusból). Három szabály él rájuk az ODINBAN — ez itt csak a felület
   * visszajelzése, hogy ne kattintson bele valaki egy garantált 403-ba:
   *   1. magadnak soha · 2. csak birtokostól · 3. szerephez SOHA nem köthető.
   * A 3. miatt a SZEREP-szerkesztő univerzumából egyszerűen KIESNEK (meg sem jelennek), az 1–2. miatt pedig a
   * per-user mátrixban letiltottak, ha a néző nem birtokolja, vagy épp önmagát szerkeszti.
   */
  const protectedKeys = useMemo(
    () => new Set(catalog.filter((p) => p.protected).map((p) => p.key)),
    [catalog],
  );
  /**
   * A SZEREP-szerkesztőben a védett jog LÁTSZIK, de ZÁROLVA (halvány + lakat + magyarázat). Korábban egyszerűen
   * kiesett a listából — csakhogy a „nincs ott" nem magyarázat: a felhasználó nem tudta meg, hogy a jog
   * LÉTEZIK, csak nem szerephez tartozik. A zár itt ABSZOLÚT: a 3. szabály (szerephez SOHA) alól a
   * break-glass platform-admin sem mentesül, mert ezt az Odin 422-vel zárja, nem jog-hiánnyal.
   */
  const roleLockReason = useCallback(
    (key: string): string | null =>
      protectedKeys.has(key) ? t('accessRights.protectedRoleLock') : null,
    [protectedKeys, t],
  );
  /**
   * Miért NEM állítható egy kulcs ennél a tagnál? `null` → állítható. A szöveg a tooltipbe megy.
   * A platform-admin (break-glass) mindent állíthat — ugyanaz a mentesség, mint a szerveren.
   */
  const protectedLockReason = useCallback(
    (key: string, targetIdentityId: string): string | null => {
      if (!protectedKeys.has(key) || isPlatformAdmin) return null;
      if (targetIdentityId === session?.sub) return t('accessRights.protectedSelfLock');
      if (!permissions.includes(key)) return t('accessRights.protectedNotHeldLock');
      return null;
    },
    [protectedKeys, isPlatformAdmin, session?.sub, permissions, t],
  );
  const featureKeysOf = useCallback(
    (r: Role): string[] => r.permissions.filter((k) => managedSet.has(k)),
    [managedSet],
  );

  const [tab, setTab] = useState<'roles' | 'members'>('roles');
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [memberQuery, setMemberQuery] = useState('');
  // SZEREPKÖR-SZŰRŐ a Felhasználók fülön (Ilcsa #72 — „ki milyen szerepben van") — a közös `ListToolbar` popoverében
  const [roleFilter, setRoleFilter] = useState<string>(ALL_FILTER_VALUE);
  // JOG-SZŰRŐ (több-választós, modul-csoportokra bontva — ugyanaz a csoportosítás és sorrend, mint a szerkesztő
  // dialógusokban: `buildGroups` a katalógus `group` + `position` mezőjéből). Az a tag illeszkedik, akinek a kiválasztott
  // jogok BÁRMELYIKE ténylegesen hatályos (szerep + személyes többlet − megvonás).
  const [permFilter, setPermFilter] = useState<string[]>([]);
  const [editRole, setEditRole] = useState<Role | null>(null);
  const [editMember, setEditMember] = useState<Member | null>(null);
  // Tag-hozzáadás / szerep-létrehozás dialógusok + tag-eltávolítás (app-offboarding) megerősítése.
  const [addMemberOpen, setAddMemberOpen] = useState(false);
  const [createRoleOpen, setCreateRoleOpen] = useState(false);
  /** A törlésre kijelölt (konzolos) szerep — a megerősítő modal célja. */
  const [deleteRoleTarget, setDeleteRoleTarget] = useState<Role | null>(null);
  const [removeTarget, setRemoveTarget] = useState<Member | null>(null);

  /**
   * A „Tag hozzáadása" dialógus SAJÁT felület-választása (Munkatárs / Külsős). SZÁNDÉKOSAN külön az oldal
   * felület-váltójától: a dialógus nyitásakor abból indul, de a felhasználó ott váltani tud anélkül, hogy a
   * mögötte lévő lista átrendeződne. A személy-katalógus ÉS a szerep-készlet is EBBŐL jön (a kettő nem
   * keveredhet: egy partner-szerep belsős személyre — és fordítva — az Odinban 422).
   */
  const [addAudience, setAddAudience] = useState<'internal' | 'partner'>(audience);

  // GLOBÁLIS identity-katalógus a „Tag hozzáadása" választóhoz — CSAK a dialógus nyitott állapotában töltjük,
  // a DIALÓGUS felülete szerint (internal → munkatársak, partner → külsős kapcsolattartók).
  const identitiesQ = api.adminIdentities.useQuery(
    ['admin-identities', addAudience],
    { query: { audience: addAudience } },
    { queryKey: ['admin-identities', addAudience], enabled: addMemberOpen },
  );
  // A dialógus KEZDŐ-SZEREP listája: az app szerepei a DIALÓGUS felületére. Ha ez egyezik az oldal
  // felületével, a TanStack ugyanarra a cache-kulcsra esik (nincs plusz kérés).
  const addRolesQ = api.adminRoles.useQuery(
    ['admin-roles', selectedApp, addAudience],
    { query: { app: selectedApp, audience: addAudience } },
    { queryKey: ['admin-roles', selectedApp, addAudience], enabled: addMemberOpen },
  );
  const addRoles = useMemo(() => {
    const list = addRolesQ.data?.status === 200 ? addRolesQ.data.body.roles : [];
    return [...list].sort(compareRoles(locale));
  }, [addRolesQ.data, locale]);
  /** A dialógus szerep-neve a DIALÓGUS felületének szerepeiből (a page-szintű `roleName` a másik felületet ismeri). */
  const addRoleName = useCallback(
    (key: string): string => addRoles.find((r) => r.key === key)?.label?.[locale] ?? roleLabel(key),
    [addRoles, locale],
  );
  // A MÁR-tag identity-k (a nyers membership-lista, típus-szűrés nélkül) → ezeket kiszűrjük a választóból.
  const memberIds = useMemo(
    () => new Set((membersQ.data?.body.members ?? []).map((m) => m.identityId)),
    [membersQ.data],
  );
  // A választható (még nem tag) személyek combobox-opciói — a dialógus felülete szerint, locale-tudatos névvel.
  const identityOptions: ComboboxOption[] = useMemo(() => {
    const list = identitiesQ.data?.status === 200 ? identitiesQ.data.body.identities : [];
    return list
      .filter((p) => !memberIds.has(p.identityId))
      .map((p) => ({
        id: p.identityId,
        label:
          formatName(
            {
              givenName: p.givenName,
              familyName: p.familyName,
              displayName: p.displayName ?? p.email,
            },
            i18n.language,
          ) ||
          (p.email ?? p.identityId),
      }));
  }, [identitiesQ.data, memberIds, i18n.language]);

  const permGroups = useMemo(() => buildGroups(featureUniverse), [buildGroups, featureUniverse]);
  /** A tag ténylegesen hatályos (kezelt) jogai: a szerepei jogai + a személyes többlet, mínusz a megvonások. */
  const effectiveKeys = useCallback(
    (m: Member): Set<string> => {
      const out = new Set<string>();
      for (const r of m.roles) {
        const role = roleByKey.get(r);
        if (role) for (const k of featureKeysOf(role)) out.add(k);
      }
      for (const o of m.permissionOverrides) {
        if (o.effect === 'grant') out.add(o.key);
        else out.delete(o.key);
      }
      return out;
    },
    [roleByKey, featureKeysOf],
  );

  const filteredMembers = useMemo(() => {
    const q = memberQuery.trim().toLowerCase();
    const collator = new Intl.Collator('hu', { sensitivity: 'base' });
    const dir = order === 'asc' ? 1 : -1;
    return [...members]
      .filter(
        (m) =>
          (roleFilter === ALL_FILTER_VALUE || m.roles.includes(roleFilter)) &&
          (permFilter.length === 0 ||
            ((eff) => permFilter.some((k) => eff.has(k)))(effectiveKeys(m))) &&
          (!q ||
            memberName(m, i18n.language).toLowerCase().includes(q) ||
            (m.email ?? '').toLowerCase().includes(q)),
      )
      .sort(
        (a, b) =>
          dir * collator.compare(memberName(a, i18n.language), memberName(b, i18n.language)),
      );
  }, [members, memberQuery, roleFilter, permFilter, effectiveKeys, order, i18n.language]);

  const invalidateRoles = (): void =>
    void qc.invalidateQueries({ queryKey: ['admin-roles', selectedApp, audience] });
  const invalidateMembers = (): void =>
    void qc.invalidateQueries({ queryKey: ['admin-members', selectedApp] });

  const patchRoles = (fn: (list: Role[]) => Role[]): void => {
    qc.setQueryData<RolesCache>(['admin-roles', selectedApp, audience], (old) =>
      old?.status === 200 ? { ...old, body: { ...old.body, roles: fn(old.body.roles) } } : old,
    );
  };
  const patchMembers = (fn: (list: Member[]) => Member[]): void => {
    qc.setQueryData<MembersCache>(['admin-members', selectedApp], (old) =>
      old?.status === 200 ? { ...old, body: { ...old.body, members: fn(old.body.members) } } : old,
    );
  };

  /** Egy tag = pontosan egy szerep (tier): optimista váltás, majd az újat rendeli és a korábbit visszavonja. */
  function onChangeRole(m: Member, roleKey: string): void {
    if (m.roles.length === 1 && m.roles[0] === roleKey) return;
    const prev = qc.getQueryData<MembersCache>(['admin-members', selectedApp]);
    patchMembers((list) =>
      list.map((x) => (x.identityId === m.identityId ? { ...x, roles: [roleKey] } : x)),
    );
    assign.mutate(
      { params: { identityId: m.identityId }, query: { app: selectedApp }, body: { roleKey } },
      {
        onSuccess: (r) => {
          if (r.status !== 200) {
            qc.setQueryData(['admin-members', selectedApp], prev);
            toast.error(t('common.notAuthorized'));
            return;
          }
          // A régi szerep(ek) visszavonása: a SAJÁT soron az Odin self-lockout fala 422-t ad (a szerep-leadás
          // másik jogkezelő dolga) — a szerep-váltás ilyenkor NEM tekinthető késznek, beszédes hibát adunk.
          const previous = m.roles.filter((k) => k !== roleKey);
          void Promise.all(
            previous.map((k) =>
              revoke
                .mutateAsync({
                  params: { identityId: m.identityId, roleKey: k },
                  query: { app: selectedApp },
                  body: {},
                })
                .then(() => null)
                // A ts-rest a nem-2xx-et ELDOBJA (a válasz-objektummal) — a hibát tehát itt kell kiolvasni,
                // különben „szerep frissítve" toast jelenne meg úgy, hogy a RÉGI szerep megmaradt.
                .catch((e: unknown) => (e ?? {}) as { status?: number }),
            ),
          ).then((results) => {
            invalidateMembers();
            const failed = results.filter((r): r is { status?: number } => r !== null);
            if (failed.length > 0) {
              // 422 = self-lockout fal (a saját szerepét senki nem veheti le) — beszédes üzenet
              toast.error(
                failed.some((f) => f.status === 422)
                  ? t('accessRights.selfLockout')
                  : t('common.error'),
              );
              return;
            }
            toast.success(t('accessRights.roleUpdated'));
          });
        },
        onError: () => {
          qc.setQueryData(['admin-members', selectedApp], prev);
          toast.error(t('common.error'));
        },
      },
    );
  }

  /** Egy szerep FEATURE-jogainak mentése (a scope-jait a BFF megőrzi). */
  function saveRolePerms(roleKey: string, keys: string[]): void {
    const prev = qc.getQueryData<RolesCache>(['admin-roles', selectedApp, audience]);
    patchRoles((list) =>
      list.map((r) =>
        r.key === roleKey
          ? { ...r, permissions: [...r.permissions.filter((p) => !managedSet.has(p)), ...keys] }
          : r,
      ),
    );
    setEditRole(null);
    updateRolePerms.mutate(
      { params: { roleKey }, query: { app: selectedApp }, body: { permissions: keys } },
      {
        onSuccess: (r) => {
          if (r.status === 200) {
            invalidateRoles();
            toast.success(t('accessRights.roleSaved'));
          } else {
            qc.setQueryData(['admin-roles', selectedApp, audience], prev);
            // Csak a valódi 403 jog-hiba — az átmeneti/Odin-oldali elutasítás (502) nem: az „Nincs jogosultság"
            // félrevezetett, amikor a partner-szerep mentését az Odin dobta vissza.
            toast.error(t(r.status === 403 ? 'common.notAuthorized' : 'common.error'));
          }
        },
        onError: () => {
          qc.setQueryData(['admin-roles', selectedApp, audience], prev);
          toast.error(t('common.error'));
        },
      },
    );
  }

  /** A felülírás-szerkesztő mentése: grant = tier fölötti többlet, deny = szerep-jog elvétele. */
  function saveOverrides(m: Member, grantKeys: string[], denyKeys: string[]): void {
    const prev = qc.getQueryData<MembersCache>(['admin-members', selectedApp]);
    const overrides: { key: string; effect: Effect }[] = [
      ...grantKeys.map((key) => ({ key, effect: 'grant' as const })),
      ...denyKeys.map((key) => ({ key, effect: 'deny' as const })),
    ];
    patchMembers((list) =>
      list.map((x) =>
        x.identityId === m.identityId
          ? { ...x, permissions: grantKeys, permissionOverrides: overrides }
          : x,
      ),
    );
    setEditMember(null);
    syncPerms.mutate(
      {
        params: { identityId: m.identityId },
        query: { app: selectedApp },
        body: { grantKeys, denyKeys },
      },
      {
        onSuccess: (r) => {
          if (r.status === 200) {
            invalidateMembers();
            toast.success(t('accessRights.overridesSaved'));
          } else {
            qc.setQueryData(['admin-members', selectedApp], prev);
            // 422 = self-lockout fal (a saját jogait senki nem szűkítheti); 403 = tényleg nincs joga
            toast.error(
              r.status === 422 ? t('accessRights.selfLockout') : t('common.notAuthorized'),
            );
          }
        },
        onError: () => {
          qc.setQueryData(['admin-members', selectedApp], prev);
          toast.error(t('common.error'));
        },
      },
    );
  }

  /**
   * „Tag hozzáadása apphoz": KIZÁRÓLAG meglévő globális személy + opcionális kezdő szerep. Siker után a
   * FELÜLET-VÁLTÓ a dialógusban választott felületre áll (különben a friss tag a szűrt listából kimaradna, és
   * a felhasználó azt látná, hogy „nem történt semmi").
   */
  function onAddMember(input: AddMemberInput): void {
    const role = input.roleKey ? { roleKey: input.roleKey } : {};
    addMemberMut.mutate(
      { query: { app: selectedApp }, body: { identityId: input.identityId, ...role } },
      {
        onSuccess: (r) => {
          if (r.status === 200) {
            setAddMemberOpen(false);
            setAudience(input.audience);
            setTab('members');
            invalidateMembers();
            toast.success(t('accessRights.memberAdded'));
          } else {
            toast.error(
              r.status === 403 ? t('common.notAuthorized') : t('accessRights.addMemberError'),
            );
          }
        },
        onError: () => toast.error(t('accessRights.addMemberError')),
      },
    );
  }

  /** Tag eltávolítása az APPBÓL (app-offboarding — a globális személyt NEM törli): optimista sor-eltávolítás + rollback. */
  function onRemoveMember(m: Member): void {
    const prev = qc.getQueryData<MembersCache>(['admin-members', selectedApp]);
    patchMembers((list) => list.filter((x) => x.identityId !== m.identityId));
    removeMemberMut.mutate(
      { params: { identityId: m.identityId }, query: { app: selectedApp }, body: {} },
      {
        onSuccess: (r) => {
          setRemoveTarget(null);
          if (r.status === 200) {
            invalidateMembers();
            toast.success(t('accessRights.memberRemoved'));
          } else {
            qc.setQueryData(['admin-members', selectedApp], prev);
            toast.error(
              r.status === 422
                ? t('accessRights.selfLockout')
                : r.status === 403
                  ? t('common.notAuthorized')
                  : t('accessRights.removeMemberError'),
            );
          }
        },
        onError: () => {
          setRemoveTarget(null);
          qc.setQueryData(['admin-members', selectedApp], prev);
          toast.error(t('accessRights.removeMemberError'));
        },
      },
    );
  }

  /** Új (üres jogú) szerep létrehozása; siker után rögtön a jog-szerkesztő nyílik az új szerepre. */
  function onCreateRole(key: string, name: string): void {
    // A dialógus egyetlen név-mezőt gyűjt → mindkét locale ugyanazt a címkét kapja (az Odin `label {hu,en}`-t vár).
    const label = { hu: name, en: name };
    createRoleMut.mutate(
      // Az új szerep a KIVÁLASZTOTT felülethez (audience) jön létre → belső és partner szerep-készlet szétválik.
      { query: { app: selectedApp }, body: { key, label, audience } },
      {
        onSuccess: (r) => {
          if (r.status === 200) {
            setCreateRoleOpen(false);
            invalidateRoles();
            toast.success(t('accessRights.roleCreated'));
            setEditRole({ key, label, permissions: [] });
          } else {
            toast.error(
              r.status === 403 ? t('common.notAuthorized') : t('accessRights.createRoleError'),
            );
          }
        },
        onError: () => toast.error(t('accessRights.createRoleError')),
      },
    );
  }

  const isForbidden = gated || isForbiddenError(rolesQ.error) || isForbiddenError(membersQ.error);
  const rolesError = gated || rolesQ.isError;
  const membersError = gated || membersQ.isError;
  const rolesLoading = !gated && (permsLoading || rolesQ.isLoading);
  const membersLoading = !gated && (permsLoading || membersQ.isLoading);

  const retry = (): void => {
    void rolesQ.refetch();
    void membersQ.refetch();
  };

  const emDash = <span className="text-muted-foreground">—</span>;

  // ---- Szerepek: a tier feature-jogai (bővíthető), szerkeszthető (superadmin szerep rögzített) ----
  const roleColumns: DataTableColumn<Role>[] = [
    {
      id: 'role',
      header: t('accessRights.tier'),
      cardLabel: t('accessRights.tier'),
      width: 'minmax(0,1fr)',
      // Szövegesen (item-cím stílus, mint a többi lista első oszlopa) + az emelt szerepkörök monokróm
      // pajzs-ikonja (Adminisztrátor = vonalas, Szuperadmin = tömör) — a Munkatársak-oldallal egységesen.
      cell: (r) => (
        <div className="flex min-w-0 flex-col items-start gap-1">
          <div className="flex min-w-0 items-center gap-1.5">
            <Text variant="itemTitle" as="span" className="truncate">
              {roleName(r.key)}
            </Text>
            <RoleIcons roles={[r.key]} />
          </div>
          {/* KATALÓGUS-TULAJDONOS: a kódból kezelt szerep szerkesztése a következő deploynál felülíródik —
              ezt látni kell, mielőtt valaki órákat tölt egy olyan szerep hangolásával, amit a sync visszaír.
              A NÉV ALATT áll (nem mellette): így a hosszú szerep-nevet nem szorítja el, és a sor-ritmus
              ugyanaz marad minden szerepnél — a jelvény kiegészítő információ, nem a cím része. Az ALAP-szerep
              (`seed`, pl. a portál-szerepek) tartalma itt szerkeszthető és a deploy nem írja vissza. */}
          {r.source === 'manifest' ? (
            <DomainBadge
              kind="roleSource"
              value="manifest"
              label={t('accessRights.roleSourceManifest')}
              className="shrink-0"
            />
          ) : r.source === 'seed' ? (
            <DomainBadge
              kind="roleSource"
              value="seed"
              label={t('accessRights.roleSourceSeed')}
              className="shrink-0"
            />
          ) : (
            <DomainBadge
              kind="roleSource"
              value="console"
              label={t('accessRights.roleSourceConsole')}
              className="shrink-0"
            />
          )}
        </div>
      ),
    },
    {
      id: 'perms',
      header: t('accessRights.tierGrants'),
      cardLabel: t('accessRights.tierGrants'),
      card: 'subtitle',
      width: 'minmax(0,3fr)',
      multiline: true,
      cell: (r) => {
        const feats = featureKeysOf(r);
        if (feats.length === 0) return emDash;
        const sorted = sortKeys(feats);
        return (
          <ExpandableCell
            items={sorted}
            getKey={(p) => p}
            visible={6}
            threshold={6}
            moreLabel={(n) => t('accessRights.more', { count: n })}
            triggerAriaLabel={t('accessRights.moreItems')}
            // Badge helyett sima szöveg — a DataTable cella-stílusát örökli (13/muted), mint az Ügyfelek-lista
            // Fiókok-oszlopának adatai.
            renderItem={(p) => <span className="block truncate">{labelFor(p)}</span>}
          />
        );
      },
    },
  ];

  // ---- Tagok: tag + szerep(tier) + per-user felülírások összefoglalója ----
  const memberColumns: DataTableColumn<Member>[] = [
    {
      id: 'name',
      header: t('accessRights.name'),
      sortable: true,
      cardLabel: t('accessRights.name'),
      width: 'minmax(0,2fr)',
      multiline: true,
      cell: (m) => {
        const name = memberName(m, i18n.language);
        return (
          <div className="flex min-w-0 items-center gap-2.5">
            <Avatar name={name} src={m.avatarUrl} size="md" accentColor={m.teamColor} />
            <div className="min-w-0">
              <Text variant="itemTitle" as="div" className="truncate">
                {name}
              </Text>
              <Text tone="tertiary" as="div" className="truncate">
                {m.email ?? ''}
              </Text>
            </div>
          </div>
        );
      },
    },
    {
      id: 'role',
      header: t('accessRights.tier'),
      cardLabel: t('accessRights.tier'),
      card: 'subtitle',
      width: 'minmax(0,1.2fr)',
      cell: (m) => {
        const currentRole = m.roles[0];
        if (isPartner) {
          // PARTNER: a jogkör FIÓKONKÉNTI — a globális „tier" itt értelmetlen volna. A sor a személy
          // fiókonkénti portál-szerepeit mutatja; a beállítás a fiók-oldalon történik (link).
          const grants = portalByIdentity.get(m.identityId) ?? [];
          if (grants.length === 0) {
            return (
              <Text variant="secondary" as="span" className="text-[12px]">
                {t('accessRights.portalNoAccess')}
              </Text>
            );
          }
          return (
            <div className="flex flex-wrap items-center gap-1.5">
              {grants.map((g) => (
                <Link
                  key={g.accountId}
                  to="/accounts/$id"
                  params={{ id: g.accountId }}
                  hash="account-contacts"
                  className="focus-visible:ring-ring rounded focus-visible:outline-none focus-visible:ring-2"
                  aria-label={t('accessRights.portalGrantAria', {
                    // Feloldatlan fiók-név = TÖRÖLT fiókon élő grant (adathiba-jelzés, nem nyers UUID).
                    account: g.accountName ?? t('common.deletedAccount'),
                    role: t(`portalAccess.roles.${g.role}`),
                  })}
                >
                  <Badge variant="secondary">
                    {g.accountName ?? t('common.deletedAccount')} ·{' '}
                    {t(`portalAccess.roles.${g.role}`)}
                  </Badge>
                </Link>
              ))}
            </div>
          );
        }
        if (!canManageRole) {
          return currentRole ? <Badge variant="secondary">{roleName(currentRole)}</Badge> : emDash;
        }
        return (
          <Select
            {...(currentRole !== undefined ? { value: currentRole } : {})}
            onValueChange={(v) => onChangeRole(m, v)}
            disabled={assign.isPending || revoke.isPending}
          >
            <SelectTrigger
              className="h-8 w-full max-w-40 text-sm"
              aria-label={t('accessRights.tier')}
            >
              <SelectValue placeholder={t('accessRights.noRole')} />
            </SelectTrigger>
            <SelectContent>
              {roleKeys.map((k) => (
                <SelectItem key={k} value={k}>
                  {roleName(k)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      },
    },
    {
      id: 'overrides',
      header: t('accessRights.overrides'),
      cardLabel: t('accessRights.overrides'),
      card: 'hidden',
      width: 'minmax(0,2fr)',
      multiline: true,
      cell: (m) => {
        // A SZEREPHEZ KÉPEST számolunk — ugyanazzal a szabállyal, mint a szerkesztő-modál. A nyers sorok
        // számolása hazudik: egy kivétel-sor megmarad akkor is, ha a jog időközben bekerült a SZEREPBE, és
        // onnantól nem ad semmit. (Mért lelet: a lista „1 többlet"-et írt, a modál „mindent örökölt"-et.)
        const { grants, denies } = meaningfulOverrides(
          m.permissionOverrides,
          new Set(
            featureKeysOf(
              roleByKey.get(m.roles[0] ?? '') ?? { key: '', label: null, permissions: [] },
            ),
          ),
        );
        if (grants.length === 0 && denies.length === 0) {
          return (
            <Text variant="secondary" as="span" className="text-[12px]">
              {t('accessRights.tierDefaultOnly')}
            </Text>
          );
        }
        return (
          <div className="flex flex-wrap items-center gap-1.5">
            {grants.length > 0 && (
              <Badge variant="success">
                +{grants.length} {t('accessRights.grantShort')}
              </Badge>
            )}
            {denies.length > 0 && (
              <Badge variant="destructive">
                −{denies.length} {t('accessRights.denyShort')}
              </Badge>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <AppShell
      // Mobilon (<md) a lebegő FAB (primaryAction) kezeli a tab-érzékeny létrehozást: Szerepkörök → „Új szerepkör",
      // Munkatársak → „Tag hozzáadása". Desktopon (≥md) ugyanezt a fejléc ListNewButton-ja adja (`hidden md:block`).
      // A jog-kapuzás változatlan: csak `access_rights.manage`-dzsel jelenik meg (a FAB is, a gomb is).
      primaryAction={
        canManageRoleCatalog
          ? {
              icon: <PlusIcon className="size-5 stroke-2" />,
              label: tab === 'members' ? t('accessRights.addMember') : t('accessRights.newRole'),
              onClick: () => {
                if (tab === 'members') {
                  setAddAudience(audience);
                  setAddMemberOpen(true);
                } else {
                  setCreateRoleOpen(true);
                }
              },
            }
          : undefined
      }
    >
      <div className="flex h-full flex-col">
        <Tabs
          value={tab}
          onValueChange={(v) => setTab(v === 'members' ? 'members' : 'roles')}
          className="flex min-h-0 flex-1 flex-col"
        >
          {/* TAB-SOR — az eszközök-oldal egységes mintája: underline-tabs + ikon, a kereső FÖLÖTT. MOBILON (<md) a
              tabok FELÜL, alattuk KÜLÖN SORBAN a választók (audience + app) — `flex-col`; desktopon (≥md) a tabok
              balra, a választók + „Új" gomb jobbra, egy sorban (`md:flex-row md:justify-between`). */}
          <div className="flex flex-col gap-2 px-4 pt-2 md:flex-row md:items-center md:justify-between md:gap-3">
            <TabsList>
              <TabsTrigger value="roles">
                <ShieldCheckIcon />
                {t('accessRights.rolesTab')}
              </TabsTrigger>
              <TabsTrigger value="members">
                <UsersIcon />
                {t('accessRights.usersTab')}
              </TabsTrigger>
            </TabsList>
            <div className="flex items-center gap-2">
              {/* FELÜLET-VÁLTÓ (audience) — MINDKÉT fülön látható: a szerep/jog-katalógust ÉS a felhasználó-listát
                  a belső rendszer (Munkatársak) vagy a partner-felület (Külsősök) dimenziójára szűri. Default: belső.
                  CSAK akkor jelenik meg, ha a választott app deklarál partner-szerepet (lásd `hasPartnerSurface`). */}
              {hasPartnerSurface && (
                <Select
                  value={audience}
                  onValueChange={(v) => setAudience(v === 'partner' ? 'partner' : 'internal')}
                >
                  <SelectTrigger
                    className="h-8 w-44 text-sm"
                    aria-label={t('accessRights.audienceLabel')}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="internal">{t('accessRights.audienceInternal')}</SelectItem>
                    <SelectItem value="partner">{t('accessRights.audienceExternal')}</SelectItem>
                  </SelectContent>
                </Select>
              )}
              {/* App-választó (KÖZPONTI konzol) — null, ha csak egy app van → a sor vizuálisan a mostani. */}
              <AppSelector
                apps={apps}
                value={selectedApp}
                onChange={setSelectedApp}
                show={showSelector}
              />
              {/* Tab-specifikus elsődleges akció (access_rights.manage): Munkatársak → tag hozzáadása, Szerepkörök → új
                  szerep. CSAK ≥md — mobilon a lebegő FAB (primaryAction) kezeli (lásd az AppShell fenti propját). */}
              {(tab === 'members' ? canManageRole : canManageRoleCatalog) && (
                <div className="hidden md:block">
                  <ListNewButton
                    label={
                      tab === 'members' ? t('accessRights.addMember') : t('accessRights.newRole')
                    }
                    onClick={() => {
                      // A dialógus felülete az OLDAL felület-váltójáról indul (utána ott is váltható).
                      if (tab === 'members') {
                        setAddAudience(audience);
                        setAddMemberOpen(true);
                      } else {
                        setCreateRoleOpen(true);
                      }
                    }}
                    enabled={tab === 'members' ? canManageRole : canManageRoleCatalog}
                    disabledTooltip={t('common.noCreatePermission')}
                  />
                </div>
              )}
            </div>
          </div>
          {/* KERESŐ / tier-megjegyzés SOR (a tabok ALATT) — AZONOS konténer (py-3 / lg:h-14) mindkét fülön, hogy a
              köz a sor FÖLÖTT és ALATT egységes legyen. Munkatársak: kereső + darabszám; Szerepkörök (nincs kereső):
              a tier-megjegyzés + darabszám ugyanebben a sorban → nincs üres sor, azonos ritmus. */}
          <div className="flex shrink-0 flex-col gap-2 px-4 py-3 lg:flex-row lg:items-center lg:justify-between lg:gap-3">
            {tab === 'members' ? (
              // a KÖZÖS lista-eszköztár: kereső + „Szűrők" popover (szerepkör) + darabszám — mint minden lista-oldalon
              <ListToolbar
                className="min-h-0 w-full p-0"
                search={{
                  value: memberQuery,
                  onChange: setMemberQuery,
                  placeholder: t('common.searchPlaceholder'),
                  ariaLabel: t('common.searchPlaceholder'),
                }}
                filterItems={[
                  {
                    kind: 'select',
                    key: 'role',
                    label: t('accessRights.filterRole'),
                    value: roleFilter,
                    onChange: setRoleFilter,
                    allValue: ALL_FILTER_VALUE,
                    // a szerep-név ADAT (a szerep-katalógusból) → nem kisbetűsítjük
                    preserveCase: true,
                    options: sortedRoles.map((r) => ({ value: r.key, label: roleName(r.key) })),
                  },
                  {
                    kind: 'multi',
                    key: 'permission',
                    label: t('accessRights.filterPermission'),
                    values: permFilter,
                    onChange: setPermFilter,
                    preserveCase: true,
                    options: permGroups.flatMap((g) =>
                      g.perms.map((p) => ({ value: p.key, label: p.label })),
                    ),
                    groups: permGroups.map((g) => ({
                      id: g.group,
                      name: g.label,
                      optionIds: g.perms.map((p) => p.key),
                    })),
                  },
                ]}
                // PARTNER-FÜL: kimondjuk, hogy a szerep itt csak LÁTSZIK — enélkül a hiányzó szerkesztő
                // „elromlott felületnek" tűnne, nem szándékos tervezésnek.
                trailing={
                  isPartner ? (
                    <Text variant="secondary" as="p" className="min-w-0 text-[12px]">
                      {t('accessRights.partnerMembersNote')}
                    </Text>
                  ) : undefined
                }
                meta={
                  !membersError && !membersLoading
                    ? t(isPartner ? 'accessRights.countExternal' : 'accessRights.countMembers', {
                        shown: filteredMembers.length,
                        total: members.length,
                      })
                    : ''
                }
              />
            ) : (
              <>
                <Text variant="secondary" as="p" className="min-w-0 text-[12px]">
                  {t(isPartner ? 'accessRights.partnerRoleNote' : 'accessRights.tierNote')}
                </Text>
                <Text variant="secondary" as="span" className="hidden shrink-0 md:inline">
                  {!rolesError && !rolesLoading
                    ? t('accessRights.countRoles', {
                        shown: sortedRoles.length,
                        total: sortedRoles.length,
                      })
                    : ''}
                </Text>
              </>
            )}
          </div>

          <TabsContent value="roles" className="mt-0 min-h-0 flex-1 overflow-hidden">
            <div className="flex h-full flex-col px-4 pb-2">
              <DataTable<Role>
                columns={roleColumns}
                rows={sortedRoles}
                getRowId={(r) => r.key}
                isLoading={rolesLoading}
                isError={rolesError}
                errorState={<ListErrorState forbidden={isForbidden} onRetry={retry} />}
                emptyState={
                  <EmptyState
                    title={t('accessRights.rolesEmpty')}
                    description={t('accessRights.rolesEmptyDesc')}
                  />
                }
                rowActionsLabel={t('common.edit')}
                rowActions={(r) =>
                  r.key === LOCKED_ROLE ? (
                    // A superadmin szerepkör rögzített → piros zár-ikon; a „Rögzített" szöveg tooltipben.
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span
                          tabIndex={0}
                          role="img"
                          aria-label={t('accessRights.roleLocked')}
                          className="focus-visible:ring-ring text-danger-600 inline-flex cursor-default rounded-sm outline-none focus-visible:ring-2"
                        >
                          <LockClosedIcon aria-hidden className="size-4" />
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>{t('accessRights.roleLocked')}</TooltipContent>
                    </Tooltip>
                  ) : canManageRoleCatalog ? (
                    <RowActions>
                      <RowActionButton
                        label={t('common.edit')}
                        onClick={() => setEditRole(r)}
                        icon={PencilSquareIcon}
                      />
                      {/* TÖRLÉS csak KONZOLOS szerepre: a kódból kezelt (manifest) szerepet a következő
                          boot-sync visszahozná — a gomb ott hamis ígéret volna. Az Odin is 422-vel zár. */}
                      {r.source === 'console' && (
                        <RowActionButton
                          label={t('accessRights.deleteRole')}
                          onClick={() => setDeleteRoleTarget(r)}
                          icon={TrashIcon}
                          destructive
                        />
                      )}
                    </RowActions>
                  ) : null
                }
              />
            </div>
          </TabsContent>

          <TabsContent value="members" className="mt-0 min-h-0 flex-1 overflow-hidden">
            <div className="flex h-full flex-col px-4 pb-2">
              <DataTable<Member>
                columns={memberColumns}
                rows={filteredMembers}
                getRowId={(m) => m.identityId}
                sort="name"
                order={order}
                onSort={() => setOrder((o) => (o === 'asc' ? 'desc' : 'asc'))}
                isLoading={membersLoading}
                isError={membersError}
                errorState={<ListErrorState forbidden={isForbidden} onRetry={retry} />}
                emptyState={
                  memberQuery.trim() || roleFilter !== ALL_FILTER_VALUE || permFilter.length > 0 ? (
                    <EmptyState
                      title={t('accessRights.noMatches')}
                      description={t('accessRights.noMatchesDesc')}
                    />
                  ) : (
                    <EmptyState
                      title={t(
                        isPartner ? 'accessRights.externalEmpty' : 'accessRights.membersEmpty',
                      )}
                      description={t(
                        isPartner
                          ? 'accessRights.externalEmptyDesc'
                          : 'accessRights.membersEmptyDesc',
                      )}
                    />
                  )
                }
                rowActionsLabel={t('common.edit')}
                actionsColWidth={canManageOverride ? '7rem' : '0'}
                rowActions={(m) =>
                  canManageOverride ? (
                    <RowActions>
                      {/* Szerkesztés (per-user felülírás). */}
                      <RowActionButton
                        label={t('common.edit')}
                        onClick={() => setEditMember(m)}
                        icon={PencilSquareIcon}
                      />
                      {/* Eltávolítás az appból (app-offboarding — NEM globális törlés). */}
                      <RowActionButton
                        label={t('accessRights.removeMember')}
                        onClick={() => setRemoveTarget(m)}
                        icon={UserMinusIcon}
                        destructive
                      />
                    </RowActions>
                  ) : null
                }
              />
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {editRole && (
        <RolePermEditorDialog
          title={`${roleName(editRole.key)} — ${t('accessRights.rolePermsSuffix')}`}
          allKeys={featureUniverse}
          buildGroups={buildGroups}
          initial={featureKeysOf(editRole)}
          lockReason={roleLockReason}
          saving={updateRolePerms.isPending}
          onClose={() => setEditRole(null)}
          onSave={(keys) => saveRolePerms(editRole.key, keys)}
        />
      )}
      {editMember && (
        <OverrideEditorDialog
          member={editMember}
          allKeys={featureUniverse}
          buildGroups={buildGroups}
          lockReason={(key) => protectedLockReason(key, editMember.identityId)}
          roleDefaults={
            new Set(
              featureKeysOf(
                roleByKey.get(editMember.roles[0] ?? '') ?? {
                  key: '',
                  label: null,
                  permissions: [],
                },
              ),
            )
          }
          saving={syncPerms.isPending}
          onClose={() => setEditMember(null)}
          onSave={(g, d) => saveOverrides(editMember, g, d)}
          memberName={memberName(editMember, i18n.language)}
          roleName={roleName}
        />
      )}

      {/* Tag hozzáadása az apphoz — KÉT FELÜLET (Munkatárs / Külsős) + kezdő szerep. Új személyt NEM hoz létre. */}
      {addMemberOpen && (
        <AddMemberDialog
          audience={addAudience}
          onAudienceChange={setAddAudience}
          showAudienceSwitch={hasPartnerSurface}
          identityOptions={identityOptions}
          loadingIdentities={identitiesQ.isLoading}
          roleKeys={addRoles.map((r) => r.key)}
          saving={addMemberMut.isPending}
          onClose={() => setAddMemberOpen(false)}
          onSubmit={onAddMember}
          roleName={addRoleName}
        />
      )}

      {/* Új szerep létrehozása (üres jogú); siker után a jog-szerkesztő nyílik. */}
      {createRoleOpen && (
        <CreateRoleDialog
          existingKeys={new Set(roleKeys)}
          saving={createRoleMut.isPending}
          onClose={() => setCreateRoleOpen(false)}
          onSubmit={onCreateRole}
        />
      )}

      {/* Szerep törlése a katalógusból — CSAK konzolos szerepre (a gomb is csak ott jelenik meg). A hibát az
          Odin üzenete adja vissza (hány hivatkozás van / miért kódból kezelt) — az teszi cselekvőképessé. */}
      <ConfirmDialog
        open={deleteRoleTarget !== null}
        onOpenChange={(open) => !open && setDeleteRoleTarget(null)}
        title={t('accessRights.deleteRoleTitle')}
        description={t('accessRights.deleteRoleBody', {
          role: deleteRoleTarget ? roleName(deleteRoleTarget.key) : '',
        })}
        confirmLabel={t('accessRights.deleteRole')}
        cancelLabel={t('common.cancel')}
        pending={deleteRoleMut.isPending}
        onConfirm={() => {
          const target = deleteRoleTarget;
          if (!target) return;
          deleteRoleMut.mutate(
            { params: { roleKey: target.key }, query: { app: selectedApp } },
            {
              onSuccess: (r) => {
                if (r.status === 200) {
                  invalidateRoles();
                  toast.success(t('accessRights.roleDeleted'));
                } else if (r.status === 409 || r.status === 422) {
                  // Az Odin üzenete: „használatban van (N hozzárendelés, M élő hozzáférés)" vagy „kódból kezelt".
                  const body = r.body as { message?: string };
                  toast.error(body.message ?? t('accessRights.deleteRoleError'));
                } else {
                  toast.error(t('accessRights.deleteRoleError'));
                }
                setDeleteRoleTarget(null);
              },
              onError: () => {
                toast.error(t('accessRights.deleteRoleError'));
                setDeleteRoleTarget(null);
              },
            },
          );
        }}
      />

      {/* Tag eltávolítása az appból (app-offboarding — a globális személyt NEM törli). */}
      <ConfirmDialog
        open={removeTarget !== null}
        onOpenChange={(open) => !open && setRemoveTarget(null)}
        title={t('accessRights.removeMemberTitle')}
        description={t('accessRights.removeMemberDesc', {
          name: removeTarget ? memberName(removeTarget, i18n.language) : '',
        })}
        confirmLabel={t('accessRights.removeMember')}
        cancelLabel={t('common.cancel')}
        pendingLabel={t('accessRights.removeMemberPending')}
        tone="danger"
        pending={removeMemberMut.isPending}
        onConfirm={() => removeTarget && onRemoveMember(removeTarget)}
      />
    </AppShell>
  );
}

/** App-tudatos csoport-építő típusa (a page adja: nem-atlas → app-katalógus, atlas → modul-csoportok). */
type BuildGroups = (
  keys: string[],
) => { group: string; label: string; perms: { key: string; label: string }[] }[];

/** Grouped checkbox szerkesztő egy SZEREP feature-jogaihoz (a scope-jait a BFF őrzi). */
/**
 * EGY JOG-SOR a két szerkesztő-modalban (szerep-jogok · per-user felülírás).
 *
 * MIÉRT KÖZÖS: a két modal ugyanazt a sort rajzolta, és a ZÁROLT alak (halvány + lakat + tooltip) csak az
 * egyikben létezett — a másik a zárolt kulcsot egyszerűen KIHAGYTA a listából. A „nincs ott" viszont nem
 * magyarázat: a felhasználó nem tudja meg, hogy a jog LÉTEZIK, csak nem ő oszthatja. Egy sorból két
 * változat pedig garantáltan elcsúszik.
 */
function PermissionCheckRow({
  label,
  checked,
  locked,
  onToggle,
  trailing,
}: {
  label: string;
  checked: boolean;
  /** A ZÁROLÁS OKA (tooltip + aria) — `null`: állítható. */
  locked: string | null;
  onToggle: () => void;
  trailing?: JSX.Element | undefined;
}): JSX.Element {
  return (
    <label
      title={locked ?? undefined}
      className={
        locked === null
          ? 'flex cursor-pointer items-center gap-2 text-sm'
          : 'flex cursor-not-allowed items-center gap-2 text-sm opacity-60'
      }
    >
      <Checkbox checked={checked} disabled={locked !== null} onCheckedChange={onToggle} />
      <span className="truncate">{label}</span>
      {locked !== null && (
        <LockClosedIcon aria-label={locked} className="text-muted-foreground size-3.5 shrink-0" />
      )}
      {trailing}
    </label>
  );
}

/** Kulcs → megjelenített címke a modál csoportjaiból (ismeretlen kulcsnál maga a kulcs). */
function labelLookup(groups: ReturnType<BuildGroups>): (key: string) => string {
  const map = new Map(groups.flatMap((g) => g.perms.map((p) => [p.key, p.label] as const)));
  return (key) => map.get(key) ?? key;
}

/**
 * Az ELŐFELTÉTEL-jelzés a két jog-szerkesztő TETEJÉN, a közös figyelem-sávval (`AttentionBanner` — ugyanaz a
 * színvilág, mint a Pénzügy lap jelzéseié; a kivétel és a hiányzó előfeltétel figyelmeztetés, a bepipálás info): mit pipált be a szerkesztő magától (a megtekintést), mit
 * vett ki magától (a megtekintéssel együtt a modul erősebb jogait), és mely jog maradt előfeltétel nélkül — ez
 * már csak a zárolt (pl. védett) jognál fordulhat elő, vagy egy korábban így mentett állapotnál.
 */
function PrerequisiteNotes({
  added,
  removed,
  missing,
  labelOf,
  className,
}: {
  added: string[];
  removed: string[];
  missing: { key: string; requires: string }[];
  labelOf: (key: string) => string;
  className?: string;
}): JSX.Element | null {
  const { t } = useTranslation();
  if (added.length === 0 && removed.length === 0 && missing.length === 0) return null;
  return (
    <div className={cn('space-y-2', className)} data-testid="permission-prerequisites">
      {removed.length > 0 && (
        <AttentionBanner
          tone="warning"
          title={t('accessRights.prereqRemoved', { list: removed.map(labelOf).join(', ') })}
        />
      )}
      {missing.length > 0 && (
        <AttentionBanner
          tone="warning"
          title={t('accessRights.prereqMissing', {
            list: missing.map((m) => `${labelOf(m.key)} → ${labelOf(m.requires)}`).join(', '),
          })}
        />
      )}
      {added.length > 0 && (
        <AttentionBanner
          tone="neutral"
          title={t('accessRights.prereqAdded', { list: added.map(labelOf).join(', ') })}
        />
      )}
    </div>
  );
}

/**
 * Egy jog bekapcsolása az ELŐFELTÉTELÉVEL együtt (`lib/permission-deps.ts`). A zárolt előfeltételt nem kapcsolja
 * be (a szerver úgyis elutasítaná) — azt a figyelmeztetés jelzi. Visszaadja, mit kapcsolt be magától.
 */
function addWithPrerequisite(
  next: Set<string>,
  key: string,
  allKeys: ReadonlySet<string>,
  lockReason: (key: string) => string | null,
): string | null {
  next.add(key);
  const view = viewPrerequisiteOf(key, allKeys);
  if (view === null || next.has(view) || lockReason(view) !== null) return null;
  next.add(view);
  return view;
}

/**
 * Egy jog kikapcsolása: ha MEGTEKINTÉS-jog, a modul erősebb jogai is kikerülnek (`lib/permission-deps.ts`). A zárolt
 * függő jogot nem veszi ki (a szerver úgyis elutasítaná) — azt a figyelmeztetés jelzi. Visszaadja, mit vett ki
 * magától.
 */
function removeWithDependents(
  next: Set<string>,
  key: string,
  allKeys: readonly string[],
  lockReason: (key: string) => string | null,
): string[] {
  next.delete(key);
  const removed = dependentsOf(key, allKeys).filter((k) => next.has(k) && lockReason(k) === null);
  for (const k of removed) next.delete(k);
  return removed;
}

/** Az automatikus be-/kivétel naplója: a friss esemény felülírja az ellenkező irányú korábbit ugyanarra a kulcsra. */
function trackAuto(prev: string[], keys: readonly string[]): string[] {
  return [...prev.filter((k) => !keys.includes(k)), ...keys];
}

function RolePermEditorDialog({
  title,
  allKeys,
  buildGroups,
  initial,
  lockReason,
  saving,
  onClose,
  onSave,
}: {
  title: string;
  allKeys: string[];
  buildGroups: BuildGroups;
  initial: string[];
  /** Miért NEM köthető ez a kulcs szerephez? `null` → állítható. A védett jogok itt MINDIG zároltak. */
  lockReason: (key: string) => string | null;
  saving: boolean;
  onClose: () => void;
  onSave: (keys: string[]) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const [checked, setChecked] = useState<Set<string>>(() => new Set(initial));
  const [autoAdded, setAutoAdded] = useState<string[]>([]);
  const [autoRemoved, setAutoRemoved] = useState<string[]>([]);
  const groups = useMemo(() => buildGroups(allKeys), [allKeys, buildGroups]);
  const allSet = useMemo(() => new Set(allKeys), [allKeys]);
  const labelOf = useMemo(() => labelLookup(groups), [groups]);
  const toggle = (key: string): void => {
    if (lockReason(key) !== null) return; // zárolt kulcs: a UI sem billentheti (a szerver 422-zné)
    const next = new Set(checked);
    if (next.has(key)) {
      const removed = removeWithDependents(next, key, allKeys, lockReason);
      setAutoRemoved((prev) => trackAuto(prev, removed));
    } else {
      const added = addWithPrerequisite(next, key, allSet, lockReason);
      if (added !== null) setAutoAdded((prev) => trackAuto(prev, [added]));
    }
    setChecked(next);
  };
  const setGroup = (keys: string[], value: boolean): void =>
    setChecked((prev) => {
      const next = new Set(prev);
      for (const k of keys) {
        if (lockReason(k) !== null) continue; // a csoport-kapcsoló sem nyúl a zárolthoz
        if (value) next.add(k);
        else next.delete(k);
      }
      return next;
    });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-[95vw] max-w-2xl sm:w-full">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        {/* a jelzés NEM görgetődik el: a lista aljáról pipálva is a modál tetején látszik */}
        <PrerequisiteNotes
          added={autoAdded.filter((k) => checked.has(k))}
          removed={autoRemoved.filter((k) => !checked.has(k))}
          missing={missingPrerequisites(checked, allKeys)}
          labelOf={labelOf}
          className="shrink-0 px-6 pb-4"
        />
        <DialogBody className="space-y-4">
          {groups.map((g) => {
            const keys = g.perms.map((p) => p.key);
            // A „mind/semmi" a ZÁROLTAKAT figyelmen kívül hagyja — különben egy csupa-védett csoportban
            // sosem váltana át, és a felirat is hazudna.
            const settable = keys.filter((k) => lockReason(k) === null);
            const allOn = settable.length > 0 && settable.every((k) => checked.has(k));
            return (
              <div key={g.group}>
                <div className="mb-1 flex items-center justify-between">
                  <Text variant="overline" as="div">
                    {g.label}
                  </Text>
                  <button
                    type="button"
                    onClick={() => setGroup(keys, !allOn)}
                    className="text-brand text-xs hover:underline"
                  >
                    {allOn ? t('common.none') : t('common.all')}
                  </button>
                </div>
                <div className="grid gap-1.5 sm:grid-cols-2">
                  {g.perms.map((p) => (
                    <PermissionCheckRow
                      key={p.key}
                      label={p.label}
                      checked={checked.has(p.key)}
                      locked={lockReason(p.key)}
                      onToggle={() => toggle(p.key)}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </DialogBody>
        <DialogFooter>
          <span className="text-muted-foreground mr-auto self-center text-xs">
            {checked.size} {t('common.selected')}
          </span>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="brand"
            onClick={() => onSave([...checked].filter((k) => lockReason(k) === null))}
            disabled={saving}
          >
            {t('common.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Per-user FELÜLÍRÁS-szerkesztő: az admin az EFFEKTÍV állapotot jelöli (mit kapjon a személy), a rendszer ebből
 * vezeti le a grant/deny override-ot a szerep-tier defaulthoz képest. Az öröklött (tier-adta) jogok külön jelölve;
 * a többlet (grant) és az elvett (deny) vizuálisan megkülönböztetve.
 */
function OverrideEditorDialog({
  member,
  allKeys,
  buildGroups,
  lockReason,
  roleDefaults,
  saving,
  onClose,
  onSave,
  memberName: name,
  roleName,
}: {
  member: Member;
  allKeys: string[];
  buildGroups: BuildGroups;
  /** VÉDETT jog: miért nem állítható ennél a tagnál? `null` → állítható. A szöveg a tooltipbe megy. */
  lockReason: (key: string) => string | null;
  roleDefaults: Set<string>;
  saving: boolean;
  onClose: () => void;
  onSave: (grantKeys: string[], denyKeys: string[]) => void;
  memberName: string;
  /** A szerep megjelenített neve (label → tier-i18n → kulcs); a szülő adja, hogy az Odin-címkét is használja. */
  roleName: (key: string) => string;
}): JSX.Element {
  const { t } = useTranslation();

  const [effective, setEffective] = useState<Set<string>>(() => {
    const eff = new Set(roleDefaults);
    for (const o of member.permissionOverrides) {
      if (o.effect === 'grant') eff.add(o.key);
      else eff.delete(o.key);
    }
    return eff;
  });

  const groups = useMemo(() => buildGroups(allKeys), [allKeys, buildGroups]);
  const allSet = useMemo(() => new Set(allKeys), [allKeys]);
  const labelOf = useMemo(() => labelLookup(groups), [groups]);
  const [autoAdded, setAutoAdded] = useState<string[]>([]);
  const [autoRemoved, setAutoRemoved] = useState<string[]>([]);

  // A megtekintés kivétele a szerepből ÖRÖKÖLT erősebb jogot is kiveszi: az effektív állapotból a mentés azt
  // megvonásként (deny) küldi — különben a személynél megmaradna a kezelés megtekintés nélkül.
  const toggle = (key: string): void => {
    if (lockReason(key)) return; // védett jog: a UI sem billentheti (a szerver 403-mal zárná)
    const next = new Set(effective);
    if (next.has(key)) {
      const removed = removeWithDependents(next, key, allKeys, lockReason);
      setAutoRemoved((prev) => trackAuto(prev, removed));
    } else {
      const added = addWithPrerequisite(next, key, allSet, lockReason);
      if (added !== null) setAutoAdded((prev) => trackAuto(prev, [added]));
    }
    setEffective(next);
  };
  // A csoportos „mind / egyik sem" SZÁNDÉKOSAN átlépi a zárolt kulcsokat: enélkül egy csoport-kattintás
  // védett jogot billentene, és a mentés 403-mal bukna — a felhasználó szempontjából megmagyarázhatatlanul.
  const setGroup = (keys: string[], value: boolean): void =>
    setEffective((prev) => {
      const next = new Set(prev);
      for (const k of keys) {
        if (lockReason(k)) continue;
        if (value) next.add(k);
        else next.delete(k);
      }
      return next;
    });

  const grantKeys = [...effective].filter((k) => !roleDefaults.has(k));
  const denyKeys = allKeys.filter((k) => roleDefaults.has(k) && !effective.has(k));

  function stateOf(key: string): 'inherited' | 'grant' | 'deny' | 'off' {
    const on = effective.has(key);
    const def = roleDefaults.has(key);
    if (on && def) return 'inherited';
    if (on && !def) return 'grant';
    if (!on && def) return 'deny';
    return 'off';
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-[95vw] max-w-2xl sm:w-full">
        <DialogHeader>
          <DialogTitle>{`${name} — ${t('accessRights.overridesSuffix')}`}</DialogTitle>
        </DialogHeader>
        {/* a jelzés NEM görgetődik el: a lista aljáról pipálva is a modál tetején látszik */}
        <PrerequisiteNotes
          added={autoAdded.filter((k) => effective.has(k))}
          removed={autoRemoved.filter((k) => !effective.has(k))}
          missing={missingPrerequisites(effective, allKeys)}
          labelOf={labelOf}
          className="shrink-0 px-6 pb-4"
        />
        <DialogBody className="space-y-4">
          <Text variant="secondary" as="p" className="text-[12px]">
            {t('accessRights.overrideHint', { role: roleName(member.roles[0] ?? '') })}
          </Text>
          <div className="space-y-4">
            {groups.map((g) => {
              const keys = g.perms.map((p) => p.key);
              const allOn = keys.every((k) => effective.has(k));
              return (
                <div key={g.group}>
                  <div className="mb-1 flex items-center justify-between">
                    <Text variant="overline" as="div">
                      {g.label}
                    </Text>
                    <button
                      type="button"
                      onClick={() => setGroup(keys, !allOn)}
                      className="text-brand text-xs hover:underline"
                    >
                      {allOn ? t('common.none') : t('common.all')}
                    </button>
                  </div>
                  <div className="grid gap-1.5 sm:grid-cols-2">
                    {g.perms.map((p) => {
                      const st = stateOf(p.key);
                      const locked = lockReason(p.key);
                      return (
                        <PermissionCheckRow
                          key={p.key}
                          label={p.label}
                          checked={effective.has(p.key)}
                          locked={locked}
                          onToggle={() => toggle(p.key)}
                          {...(st === 'off'
                            ? {}
                            : {
                                trailing: (
                                  <DomainBadge
                                    kind="permissionEffect"
                                    value={st}
                                    label={t(`accessRights.${st}Tag`)}
                                    className="ml-auto shrink-0"
                                  />
                                ),
                              })}
                        />
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </DialogBody>
        <DialogFooter>
          <span className="text-muted-foreground mr-auto self-center text-xs">
            {t('accessRights.overrideSummary', {
              grants: grantKeys.length,
              denies: denyKeys.length,
            })}
          </span>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button variant="brand" onClick={() => onSave(grantKeys, denyKeys)} disabled={saving}>
            {t('common.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * „Tag hozzáadása apphoz" — KÉT FELÜLET (fül): **Munkatárs** (`internal`) és **Külsős** (`partner`). Mindkettőn
 * KIZÁRÓLAG MÁR LÉTEZŐ személy választható (kereshető combobox, a már-tagokat kiszűrve) + opcionális kezdő szerep
 * az adott felület szerep-készletéből.
 *
 * MIÉRT NINCS „új személy" mód (user-döntés): ez a felület nem tudja, belsős munkatársat vagy külsős
 * kapcsolattartót vesznek-e fel — a kettő MÁS adat-vertikum (munkaviszony-adat vs. ügyfél-kötés), és MÁS felület
 * kezeli (Munkatársak / Kapcsolattartók). Az itteni provisioning „félig felvett" személyt hagyott: identity
 * app-tagsággal, de HR-/ügyfél-adat nélkül — tehát MINDKÉT listából kimaradva. A szűkítés a szerződésben is áll.
 *
 * A KINÉZET VÁLTOZATLAN: ugyanaz a két pill-fül a dialógus tetején, alatta a személy- és a szerep-választó.
 */
function AddMemberDialog({
  audience,
  onAudienceChange,
  showAudienceSwitch,
  identityOptions,
  loadingIdentities,
  roleKeys,
  saving,
  onClose,
  onSubmit,
  roleName,
}: {
  /** A választott FELÜLET — a személy-katalógust ÉS a szerep-készletet is ez határozza meg (a szülő tölti). */
  audience: 'internal' | 'partner';
  onAudienceChange: (a: 'internal' | 'partner') => void;
  /** Van-e egyáltalán partner-felülete a választott appnak (a katalógusa deklarál-e partner-szerepet). */
  showAudienceSwitch: boolean;
  identityOptions: ComboboxOption[];
  loadingIdentities: boolean;
  roleKeys: string[];
  saving: boolean;
  onClose: () => void;
  onSubmit: (input: AddMemberInput) => void;
  /** A szerep megjelenített neve (label → tier-i18n → kulcs); a szülő adja, a VÁLASZTOTT felület szerepeiből. */
  roleName: (key: string) => string;
}): JSX.Element {
  const { t } = useTranslation();
  const [identityId, setIdentityId] = useState<string | null>(null);
  const [roleKey, setRoleKey] = useState<string>(roleKeys[0] ?? '');

  // FELÜLET-VÁLTÁSKOR a választás ÜRÜL: a másik felület személye/szerepe itt érvénytelen (egy partner-szerep
  // belsős személyre — és fordítva — az Odinban 422). A szerep az új készlet ELSŐ elemére áll.
  useEffect(() => {
    setIdentityId(null);
  }, [audience]);
  useEffect(() => {
    setRoleKey((prev) => (roleKeys.includes(prev) ? prev : (roleKeys[0] ?? '')));
  }, [roleKeys]);

  // Lokális (in-memory) keresés a betöltött, még-nem-tag identity-opciók fölött.
  const search = useCallback(
    (q: string): Promise<ComboboxOption[]> => {
      const needle = q.trim().toLowerCase();
      const list = needle
        ? identityOptions.filter((o) => o.label.toLowerCase().includes(needle))
        : identityOptions;
      return Promise.resolve(list.slice(0, 50));
    },
    [identityOptions],
  );

  const canSubmit = identityId !== null;

  const submit = (): void => {
    if (identityId === null) return;
    const input: AddMemberInput = { audience, identityId };
    if (roleKey) input.roleKey = roleKey;
    onSubmit(input);
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-[95vw] max-w-lg sm:w-full">
        <DialogHeader>
          <DialogTitle>{t('accessRights.addMemberTitle')}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <Text variant="secondary" as="p" className="text-[12px]">
            {t('accessRights.addMemberDesc')}
          </Text>
          {/* FELÜLET-választó — a KÖZÖS `Segmented` primitív (ugyanez a megjelenés az üzenet-írás típus- és
              címzés-választóján; a megjelenés innen került a primitívbe). */}
          {/* Partner-felület nélküli appnál nincs mit választani: a dialógus egyből a belső felületen áll. */}
          {showAudienceSwitch && (
            <Segmented
              value={audience}
              onChange={(v: string) => onAudienceChange(v === 'partner' ? 'partner' : 'internal')}
              options={[
                { value: 'internal', label: t('accessRights.addAudienceInternal') },
                { value: 'partner', label: t('accessRights.addAudienceExternal') },
              ]}
              aria-label={t('accessRights.addMemberTitle')}
              className="grid w-full grid-cols-2"
            />
          )}

          <div className="space-y-1.5">
            <Label>{t('accessRights.personLabel')}</Label>
            <EntityCombobox
              value={identityId}
              onChange={setIdentityId}
              search={search}
              placeholder={loadingIdentities ? t('common.loading') : t('accessRights.selectPerson')}
              aria-label={t('accessRights.personLabel')}
            />
            {/* Üres katalógus (nincs választható személy ezen a felületen) — a felvétel máshol történik, ezt KI
                KELL írni, különben a felhasználó egy néma, üres választót lát. */}
            {!loadingIdentities && identityOptions.length === 0 && (
              <Text variant="secondary" as="p" className="text-[12px]">
                {audience === 'internal'
                  ? t('accessRights.noInternalCandidates')
                  : t('accessRights.noPartnerCandidates')}
              </Text>
            )}
          </div>

          <div className="space-y-1.5">
            <Label>{t('accessRights.initialRole')}</Label>
            {roleKeys.length === 0 ? (
              <Text variant="secondary" as="p" className="text-[12px]">
                {t('accessRights.noAppRoles')}
              </Text>
            ) : (
              <Select value={roleKey} onValueChange={setRoleKey}>
                <SelectTrigger aria-label={t('accessRights.initialRole')}>
                  <SelectValue placeholder={t('accessRights.noRole')} />
                </SelectTrigger>
                <SelectContent>
                  {roleKeys.map((k) => (
                    <SelectItem key={k} value={k}>
                      {roleName(k)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button variant="brand" onClick={submit} disabled={saving || !canSubmit}>
            {saving ? t('common.loading') : t('accessRights.addMemberSubmit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Új (üres jogú) szerep létrehozása: kulcs (slug) + megjelenített név. A jogokat utána a szerep-szerkesztő állítja. */
function CreateRoleDialog({
  existingKeys,
  saving,
  onClose,
  onSubmit,
}: {
  existingKeys: Set<string>;
  saving: boolean;
  onClose: () => void;
  onSubmit: (key: string, name: string) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const [key, setKey] = useState('');
  const [name, setName] = useState('');
  const keyValid = /^[a-z0-9_-]+$/.test(key.trim());
  const keyDup = existingKeys.has(key.trim());
  const canSubmit = keyValid && !keyDup && name.trim().length > 0;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-[95vw] max-w-md sm:w-full">
        <DialogHeader>
          <DialogTitle>{t('accessRights.newRoleTitle')}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <Text variant="secondary" as="p" className="text-[12px]">
            {t('accessRights.newRoleDesc')}
          </Text>
          <div className="space-y-1.5">
            <Label htmlFor="create-role-name">{t('accessRights.roleNameLabel')}</Label>
            <Input
              id="create-role-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('accessRights.roleNamePlaceholder')}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="create-role-key">{t('accessRights.roleKeyLabel')}</Label>
            <Input
              id="create-role-key"
              value={key}
              onChange={(e) => setKey(e.target.value.toLowerCase())}
              placeholder={t('accessRights.roleKeyPlaceholder')}
            />
            <Text tone="tertiary" as="p" className="text-[11px]">
              {keyDup ? t('accessRights.roleKeyDup') : t('accessRights.roleKeyHint')}
            </Text>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="brand"
            onClick={() => canSubmit && onSubmit(key.trim(), name.trim())}
            disabled={saving || !canSubmit}
          >
            {saving ? t('common.loading') : t('accessRights.createRoleSubmit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
